/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import { StyleSheet, View } from 'react-native';
import type { AddressType } from '@sweetgreen/garnish';
import {
  AddressOrder,
  AddressTypes,
  BodyText,
  Ledger as LedgerComponent,
  SectionHeader,
  theme,
  VStack,
} from '@sweetgreen/garnish';

import { useLedgerItems, usePreferencesText } from '@order/components';
import type { CourierDetails } from '@order/graphql';
import type { Ledger, PartialLineItem } from '@order/graphql';
import { DeliveryPreferenceType, FlattenedOrderStatuses } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { CourierInfo } from './OrderStatusCourierInfo';
import { OrderStatusLineItems } from './OrderStatusLineItems';

export const OrderStatusDetails = (props: OrderStatusDetailsProps) => {
  const showCourierInfoForVStackSpacing =
    props.orderType === 'delivery' &&
    props.orderStatus === FlattenedOrderStatuses.Delivering &&
    props.courierName &&
    props.courierPhone; /* note: VStack adds spacing for null inner children. */

  return (
    <VStack testID="order-status-details" customSpacing={40}>
      {showCourierInfoForVStackSpacing ? <CourierInfo {...props} /> : null}
      <AddressSection {...props} />
      <OrderSection {...props} />
    </VStack>
  );
};

export const AddressSection = ({
  orderType,
  deliveryAddressName,
  deliveryAddressStreet,
  deliveryAddressSecondaryStreet,
  deliveryAddressCity,
  deliveryAddressState,
  deliveryAddressZipCode,
  deliveryAddressNotes,
  deliveryPreference,
  locationName,
  locationAddress,
  locationCity,
  locationState,
  locationZipCode,
  dropoffLocation,
}: OrderStatusDetailsProps) => {
  const { t } = useLocalizationContext();
  const deliveryPreferenceText = usePreferencesText(
    deliveryPreference ?? DeliveryPreferenceType.LeaveAtDoor,
  );
  const isDelivery = orderType === 'delivery';

  return (
    <View>
      <SectionHeader
        style={styles.addressHeader}
        heading={
          orderType === AddressTypes.delivery
            ? t('general.delivery-location')
            : orderType === AddressTypes.pickup
            ? t('general.pickup-location')
            : t('general.outpost-location')
        }
      />
      <AddressOrder
        addressType={orderType}
        name={isDelivery ? deliveryAddressName : locationName}
        street={isDelivery ? deliveryAddressStreet : locationAddress}
        city={isDelivery ? deliveryAddressCity : locationCity}
        state={isDelivery ? deliveryAddressState : locationState}
        zipCode={isDelivery ? deliveryAddressZipCode : locationZipCode}
        secondaryStreet={deliveryAddressSecondaryStreet}
        hideIcon
      />
      {dropoffLocation ? (
        <View style={styles.preferences}>
          <BodyText size={4}>{dropoffLocation}</BodyText>
        </View>
      ) : null}
      {isDelivery ? (
        <View style={styles.preferences}>
          <BodyText size={4}>{deliveryPreferenceText}</BodyText>
          <BodyText size={4}>{deliveryAddressNotes}</BodyText>
        </View>
      ) : null}
    </View>
  );
};

export const OrderSection = ({
  lineItems,
  ledger,
}: OrderStatusDetailsProps) => {
  const { t } = useLocalizationContext();

  const ledgerTip = ledger.tip ?? 0;
  const ledgerItems = useLedgerItems({
    ledger,
    showTip: ledgerTip > 0,
    showTotal: true,
    showTaxes: true,
    showReward: true,
  });

  return (
    <View>
      <View>
        <SectionHeader heading={t('general.your-order')} />
        <OrderStatusLineItems lineItems={lineItems} />
      </View>
      <LedgerComponent
        style={styles.ledger}
        testID="order-status-details-ledger"
        hasTopBorder={true}
        items={ledgerItems}
      />
    </View>
  );
};

export type OrderStatusDetailsProps = {
  readonly orderType: AddressType;
  readonly orderStatus?: FlattenedOrderStatuses;
  readonly deliveryAddressName?: string;
  readonly deliveryAddressStreet?: string;
  readonly deliveryAddressSecondaryStreet?: string;
  readonly deliveryAddressCity?: string;
  readonly deliveryAddressState?: string;
  readonly deliveryAddressZipCode?: string;
  readonly deliveryAddressNotes?: string;
  readonly deliveryPreference?: DeliveryPreferenceType;
  readonly locationName?: string;
  readonly locationAddress?: string;
  readonly locationCity?: string;
  readonly locationState?: string;
  readonly locationZipCode?: string;
  readonly lineItems: readonly PartialLineItem[];
  readonly dropoffLocation?: string;
  readonly ledger: Partial<Ledger>;
  readonly courierName?: CourierDetails['name'];
  readonly courierPhone?: CourierDetails['phone'];
};

const styles = StyleSheet.create({
  addressHeader: {
    marginBottom: theme.spacing['6'],
  },
  ledger: {
    paddingBottom: 0,
  },
  preferences: {
    marginTop: theme.spacing['4'],
  },
});
